import type { SimCareerView, SimTramoPuzzle, SimTramoResponse, SimTramoToday } from '@cruz/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { simApi, simErrorMessage } from './api.ts';
import { Portrait } from './Cast.tsx';
import { AchievementCard, Delta } from './Changes.tsx';
import { ErrandNotes } from './Errands.tsx';
import { ShareButton } from './Share.tsx';
import { Board, Intro, seconds } from './TramoBoard.tsx';
import { card, Link, Notice, primaryButton, secondaryButton, Working } from './ui.tsx';

/*
 * «Ordena el tramo», the player's daily game: the intro, the board, the result and the
 * card on the day screen. A tramo put in order can go to friends as a challenge.
 */

type Phase =
  | { at: 'loading' }
  | { at: 'error'; message: string }
  | { at: 'intro'; puzzle: SimTramoPuzzle }
  | { at: 'play'; puzzle: SimTramoPuzzle }
  | { at: 'sending' }
  | { at: 'result'; puzzle: SimTramoPuzzle; result: SimTramoResponse['result'] };

export function TramoGame({
  onCareer,
  onDone,
  onSkip,
}: {
  onCareer: (c: SimCareerView) => void;
  /** After the result */
  onDone: () => void;
  /** «Ahora no»: only when offered between events */
  onSkip?: () => void;
}) {
  const [phase, setPhase] = useState<Phase>({ at: 'loading' });
  useEffect(() => {
    let alive = true;
    simApi
      .tramo()
      .then((r) => alive && setPhase({ at: 'intro', puzzle: r.puzzle }))
      .catch((err: unknown) => alive && setPhase({ at: 'error', message: simErrorMessage(err) }));
    return () => {
      alive = false;
    };
  }, []);

  const finish = useCallback(
    async (puzzle: SimTramoPuzzle, taps: string[], ms: number) => {
      setPhase({ at: 'sending' });
      try {
        const r = await simApi.playTramo(taps, ms);
        onCareer(r.career);
        setPhase({ at: 'result', puzzle, result: r.result });
        scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        setPhase({ at: 'error', message: simErrorMessage(err) });
      }
    },
    [onCareer],
  );

  switch (phase.at) {
    case 'loading':
      return <Working label="Reuniendo al tramo…" />;
    case 'sending':
      return <Working label="El diputado repasa la fila…" />;
    case 'error':
      return (
        <div className="space-y-3">
          <Notice tone="error">{phase.message}</Notice>
          <button type="button" className={secondaryButton} onClick={onSkip ?? onDone}>
            Seguir
          </button>
        </div>
      );
    case 'intro':
      return (
        <Intro
          puzzle={phase.puzzle}
          onStart={() => {
            setPhase({ at: 'play', puzzle: phase.puzzle });
            scrollTo({ top: 0 });
          }}
          onSkip={onSkip}
        />
      );
    case 'play':
      return (
        <Board puzzle={phase.puzzle} onFinish={(taps, ms) => void finish(phase.puzzle, taps, ms)} />
      );
    case 'result':
      return <Result puzzle={phase.puzzle} result={phase.result} onDone={onDone} />;
  }
}

const VERDICTS: Record<number, (complete: boolean) => { title: string; deputy: string }> = {
  3: () => ({ title: '¡Tramo perfecto!', deputy: '«Así da gusto. Ni una vela fuera de sitio.»' }),
  2: () => ({ title: 'Buen tramo', deputy: '«Bien. Esa fila, un pelín más recta, y perfecto.»' }),
  1: (complete) =>
    complete
      ? {
          title: 'Tramo formado, con tropiezos',
          deputy: '«Ha salido. Mañana lo repasamos con calma.»',
        }
      : { title: 'Se acabó el tiempo', deputy: '«Deja, que el resto lo coloco yo. Mañana, más.»' },
};

function Result({
  puzzle,
  result,
  onDone,
}: {
  puzzle: SimTramoPuzzle;
  result: SimTramoResponse['result'];
  onDone: () => void;
}) {
  const v = VERDICTS[result.stars]!(result.complete);
  const c = result.changes;
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => titleRef.current?.focus({ preventScroll: true }), []);
  return (
    <article aria-labelledby="tramo-result" className="space-y-4">
      <div className="animate-crown rounded-2xl border-2 border-gold bg-purple px-5 py-6 text-center text-cream dark:bg-night-700">
        <p
          className="text-4xl tracking-[0.2em] text-gold-300"
          role="img"
          aria-label={`${result.stars} de 3 estrellas`}
        >
          {'★'.repeat(result.stars)}
          <span className="opacity-30">{'★'.repeat(3 - result.stars)}</span>
        </p>
        <h1
          id="tramo-result"
          ref={titleRef}
          tabIndex={-1}
          className="mt-2 font-serif text-[2rem] leading-tight font-semibold outline-none"
        >
          {v.title}
        </h1>
        <p className="mt-2 text-cream/85">
          {result.placed} de {result.total} en su sitio
          {result.complete && <> · {seconds(result.ms)} s</>} ·{' '}
          {result.errors === 1 ? '1 fallo' : `${result.errors} fallos`}
        </p>
        {result.best && result.complete && (
          <p className="mt-2 text-sm font-semibold tracking-wide text-gold-300 uppercase">
            Tu mejor tiempo
          </p>
        )}
      </div>
      <div className={`${card} px-5 py-4`}>
        <div className="flex items-start gap-3">
          {puzzle.deputy && (
            <Portrait
              id="tramo_deputy"
              name={puzzle.deputy.name}
              src={puzzle.deputy.portrait}
              size={44}
            />
          )}
          <p className="text-[1.05rem] italic">
            {puzzle.deputy ? `${puzzle.deputy.name.split(/[\s,]/)[0]}: ` : ''}
            {v.deputy}
          </p>
        </div>
        {(c.stats.length > 0 || c.relation) && (
          <ul aria-label="Cambios" className="mt-4 flex flex-wrap gap-2">
            {c.stats.map((s) => (
              <Delta key={s.key} label={s.label} delta={s.delta} />
            ))}
            {c.relation && (
              <Delta label={`Relación con ${c.relation.name}`} delta={c.relation.delta} />
            )}
          </ul>
        )}
        {c.errands.length > 0 && (
          <ul aria-label="Encargos" className="mt-4 space-y-2">
            <ErrandNotes errands={c.errands} />
          </ul>
        )}
      </div>
      {c.achievements.map((a) => (
        <AchievementCard key={a.id} achievement={a} />
      ))}
      {result.complete && (
        <div className={`${card} space-y-3 px-5 py-4`}>
          <p className="text-[1.05rem]">
            ¿Lo haría mejor alguien que conozcas? Mándale este mismo tramo y a ver quién gana.
          </p>
          <ShareButton request={{ kind: 'tramo', ref: result.day }} label="Retar a un amigo" />
        </div>
      )}
      <p className="text-center text-ink-soft dark:text-cream/75">
        {result.level < 5 && result.stars === 3
          ? 'Mañana, otro tramo… y un poco más difícil.'
          : 'Mañana, otro tramo.'}
      </p>
      <button type="button" className={primaryButton} onClick={onDone}>
        Seguir
      </button>
    </article>
  );
}

/** On the day screen: today's tramo, to play or already played. */
export function TramoCard({ tramo, onPlay }: { tramo: SimTramoToday; onPlay: () => void }) {
  return (
    <section aria-labelledby="tramo-card" className={`${card} px-5 py-4`}>
      <p className="text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase dark:text-gold-300">
        Minijuego del día
      </p>
      <h2 id="tramo-card" className="mt-1 font-serif text-2xl font-semibold">
        Ordena el tramo
      </h2>
      {tramo.available ? (
        <>
          <p className="mt-1 text-[1.02rem]">
            Medio minuto para poner a cada nazareno en su sitio. Nivel {tramo.level}.
          </p>
          <button type="button" className={`${primaryButton} mt-3`} onClick={onPlay}>
            Ordenar el tramo
          </button>
        </>
      ) : (
        <p className="mt-1 text-[1.02rem]">
          Hoy:{' '}
          <span className="text-gold-700 dark:text-gold-300" aria-hidden>
            {'★'.repeat(tramo.stars ?? 0)}
            <span className="opacity-30">{'★'.repeat(3 - (tramo.stars ?? 0))}</span>
          </span>
          <span className="sr-only">{tramo.stars ?? 0} de 3 estrellas</span>
          {tramo.best_ms !== null && <> · Tu mejor tiempo: {seconds(tramo.best_ms)} s</>}. Mañana,
          otro.
        </p>
      )}
      {!tramo.available && (
        <p className="mt-2 text-ink-soft dark:text-cream/75">
          ¿Te quedas con ganas? Juega <Link to="/simulador/tramo">el tramo del día</Link>, el mismo
          para todo el mundo.
        </p>
      )}
      {!tramo.available && tramo.challenge_day && (
        <div className="mt-3">
          <ShareButton
            request={{ kind: 'tramo', ref: tramo.challenge_day }}
            label="Retar a un amigo"
          />
        </div>
      )}
    </section>
  );
}
